/**
 * Sticker manifest — `kaether:sticker:pack:v1:` codec with Ed25519 author signature.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/StickerManifest.swift`.
 *
 * ## Overview
 *
 * A sticker pack is a named collection of 1–200 stickers. The pack is
 * published by an author who signs the canonical bytes of the manifest with
 * their Ed25519 private key. Receivers verify the signature before accepting
 * the manifest, so a server cannot swap stickers or forge packs.
 *
 * ## Wire format
 *
 *   kaether:sticker:pack:v1:<base64url(payload)>
 *
 * Payload byte layout (all multi-byte integers little-endian):
 *
 *   offset  0 — 1 byte    schema  (0x01 for v1)
 *   offset  1 — 2 bytes   nameLen (UInt16 LE, UTF-8 byte count of pack name)
 *   offset  3 — nameLen   name    (UTF-8)
 *   offset  3+nameLen — 2 bytes sticker count (UInt16 LE, 1..200)
 *   for each sticker (count times):
 *     2 bytes  idLen      (UInt16 LE)
 *     idLen    id         (UTF-8)
 *     2 bytes  urlLen     (UInt16 LE)
 *     urlLen   url        (UTF-8, must pass validateProxyURL)
 *     2 bytes  altTextLen (UInt16 LE, may be 0)
 *     altTextLen altText  (UTF-8)
 *   64 bytes signature (Ed25519 over bytes[0..length-64), i.e. everything before sig)
 *   32 bytes authorPublicKey (Ed25519 raw)
 *
 * Canonical bytes for signature = everything in the payload BEFORE the 64-byte
 * signature suffix (schema + name + sticker records). The author public key is
 * appended AFTER the signature in the payload but is NOT covered by the
 * signature — it is the key against which the signature is verified.
 *
 * ## Validation on decode
 *
 *  - Byte layout must be well-formed (no truncation, no length overflows).
 *  - Schema byte must be 0x01.
 *  - Sticker count: 1..200 (0 and >200 are both rejected).
 *  - Sticker IDs must be unique within the pack.
 *  - Every sticker URL must pass `validateProxyURL` from GifProxyPolicy.
 *  - The 64-byte Ed25519 signature must verify against the embedded author key.
 *
 * Pure logic / SubtleCrypto only. No `fetch()`. No SolidJS imports.
 */

import { validateProxyURL, PROXY_BASE_URL as _PROXY_BASE_URL } from "./GifProxyPolicy";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const STICKER_PACK_PREFIX = "kaether:sticker:pack:v1:" as const;
export const STICKER_PACK_CURRENT_SCHEMA = 0x01;
export const STICKER_PACK_MAX_STICKERS = 200;
export const STICKER_PACK_MIN_STICKERS = 1;

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type StickerManifestErrorKind =
  | { tag: "wrongPrefix" }
  | { tag: "malformed"; reason: string }
  | { tag: "unsupportedSchema"; schema: number }
  | { tag: "emptyPack" }
  | { tag: "tooManyStickers"; count: number }
  | { tag: "duplicateStickerIds"; id: string }
  | { tag: "invalidStickerURL"; url: string }
  | { tag: "invalidSignature" };

export class StickerManifestError extends Error {
  readonly kind: StickerManifestErrorKind;

  constructor(kind: StickerManifestErrorKind) {
    super(StickerManifestError.message(kind));
    this.name = "StickerManifestError";
    this.kind = kind;
  }

  private static message(k: StickerManifestErrorKind): string {
    switch (k.tag) {
      case "wrongPrefix":
        return `not a sticker pack — missing prefix "${STICKER_PACK_PREFIX}"`;
      case "malformed":
        return `malformed sticker pack payload: ${k.reason}`;
      case "unsupportedSchema":
        return `unsupported sticker pack schema: 0x${k.schema.toString(16)}`;
      case "emptyPack":
        return "sticker pack must contain at least 1 sticker";
      case "tooManyStickers":
        return `sticker pack exceeds ${STICKER_PACK_MAX_STICKERS}-sticker limit (got ${k.count})`;
      case "duplicateStickerIds":
        return `duplicate sticker id in pack: "${k.id}"`;
      case "invalidStickerURL":
        return `sticker URL does not pass proxy validation: ${k.url}`;
      case "invalidSignature":
        return "invalid author signature on sticker pack";
    }
  }
}

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export interface StickerEntry {
  /** Unique identifier within the pack. */
  readonly id: string;
  /** Absolute proxy URL — must pass `validateProxyURL`. */
  readonly url: string;
  /** Optional alt text for accessibility. */
  readonly altText: string;
}

export interface StickerPack {
  /** Human-readable name for the pack. */
  readonly name: string;
  /** Stickers in the pack. 1..200 entries, unique IDs. */
  readonly stickers: readonly StickerEntry[];
  /** Ed25519 author public key (32 bytes). */
  readonly authorPublicKey: Uint8Array;
  /** Ed25519 signature over canonical bytes (64 bytes). */
  readonly signature: Uint8Array;
}

// ---------------------------------------------------------------------------
// SubtleCrypto helpers
// ---------------------------------------------------------------------------

async function importEd25519PublicKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    raw,
    { name: "Ed25519" },
    false,
    ["verify"],
  );
}

async function importEd25519PrivateKey(raw: Uint8Array): Promise<CryptoKey> {
  // PKCS#8-wrapped Ed25519 private key.
  // The SubtleCrypto Ed25519 private key import expects PKCS#8 DER.
  // We build the minimal PKCS#8 wrapper around the 32-byte private key seed.
  //
  // PKCS#8 structure for Ed25519 (RFC 8410):
  //   SEQUENCE {
  //     INTEGER 0                        — version
  //     SEQUENCE { OID 1.3.101.112 }    — Ed25519 AlgorithmIdentifier
  //     OCTET STRING { OCTET STRING { <32 bytes seed> } }
  //   }
  //
  // Hex-encoded DER prefix for a 32-byte Ed25519 private key in PKCS#8:
  const pkcs8Prefix = new Uint8Array([
    0x30, 0x2e,       // SEQUENCE (46 bytes)
    0x02, 0x01, 0x00, // INTEGER 0 (version)
    0x30, 0x05,       // SEQUENCE (5 bytes — AlgorithmIdentifier)
    0x06, 0x03, 0x2b, 0x65, 0x70, // OID 1.3.101.112 (id-EdDSA / Ed25519)
    0x04, 0x22,       // OCTET STRING (34 bytes)
    0x04, 0x20,       // OCTET STRING (32 bytes — the seed)
  ]);
  const pkcs8 = new Uint8Array(pkcs8Prefix.length + raw.length);
  pkcs8.set(pkcs8Prefix);
  pkcs8.set(raw, pkcs8Prefix.length);

  return crypto.subtle.importKey(
    "pkcs8",
    pkcs8,
    { name: "Ed25519" },
    false,
    ["sign"],
  );
}

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------

const enc = new TextEncoder();
const dec = new TextDecoder("utf-8", { fatal: true });

function base64UrlEncode(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array | null {
  try {
    const padded = s.replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - (padded.length % 4)) % 4;
    const b64 = padded + "=".repeat(padding);
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      out[i] = binary.charCodeAt(i);
    }
    return out;
  } catch {
    return null;
  }
}

function writeUint16LE(buf: Uint8Array, offset: number, value: number): void {
  buf[offset] = value & 0xff;
  buf[offset + 1] = (value >>> 8) & 0xff;
}

function readUint16LE(buf: Uint8Array, offset: number): number {
  return buf[offset] | (buf[offset + 1] << 8);
}

// ---------------------------------------------------------------------------
// Canonical bytes (everything the signature covers)
// ---------------------------------------------------------------------------

function buildCanonicalBytes(pack: {
  name: string;
  stickers: readonly StickerEntry[];
}): Uint8Array {
  const nameBytes = enc.encode(pack.name);

  // Pre-encode all sticker fields to compute total size.
  const encodedStickers = pack.stickers.map((s) => ({
    idBytes: enc.encode(s.id),
    urlBytes: enc.encode(s.url),
    altBytes: enc.encode(s.altText),
  }));

  let size =
    1 + // schema
    2 + nameBytes.length + // nameLen + name
    2; // sticker count

  for (const es of encodedStickers) {
    size +=
      2 + es.idBytes.length +
      2 + es.urlBytes.length +
      2 + es.altBytes.length;
  }

  const buf = new Uint8Array(size);
  let offset = 0;

  buf[offset++] = STICKER_PACK_CURRENT_SCHEMA;

  writeUint16LE(buf, offset, nameBytes.length);
  offset += 2;
  buf.set(nameBytes, offset);
  offset += nameBytes.length;

  writeUint16LE(buf, offset, pack.stickers.length);
  offset += 2;

  for (const es of encodedStickers) {
    writeUint16LE(buf, offset, es.idBytes.length);
    offset += 2;
    buf.set(es.idBytes, offset);
    offset += es.idBytes.length;

    writeUint16LE(buf, offset, es.urlBytes.length);
    offset += 2;
    buf.set(es.urlBytes, offset);
    offset += es.urlBytes.length;

    writeUint16LE(buf, offset, es.altBytes.length);
    offset += 2;
    buf.set(es.altBytes, offset);
    offset += es.altBytes.length;
  }

  return buf;
}

// ---------------------------------------------------------------------------
// Key generation
// ---------------------------------------------------------------------------

/**
 * Generates a fresh Ed25519 key pair suitable for authoring sticker packs.
 *
 * @returns `{ publicKeyBytes, publicKey, privateKey }` where `publicKeyBytes`
 *   is the raw 32-byte public key to embed in the manifest.
 */
export async function generateAuthorKeyPair(): Promise<{
  publicKeyBytes: Uint8Array;
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}> {
  const pair = await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"],
  );
  const pubRaw = await crypto.subtle.exportKey("raw", pair.publicKey);
  return {
    publicKeyBytes: new Uint8Array(pubRaw),
    publicKey: pair.publicKey,
    privateKey: pair.privateKey,
  };
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

/**
 * Encodes and signs a sticker pack into the `kaether:sticker:pack:v1:` wire format.
 *
 * @param name      Human-readable pack name.
 * @param stickers  1–200 sticker entries with unique IDs and proxy-validated URLs.
 * @param authorPrivateKey  Ed25519 CryptoKey with `sign` usage.
 * @param authorPublicKeyBytes  Raw 32-byte public key to embed.
 * @returns Wire-format string `kaether:sticker:pack:v1:<base64url>`.
 * @throws {StickerManifestError} If the pack is invalid before encoding.
 */
export async function encodeStickerPack(
  name: string,
  stickers: readonly StickerEntry[],
  authorPrivateKey: CryptoKey,
  authorPublicKeyBytes: Uint8Array,
): Promise<string> {
  if (stickers.length < STICKER_PACK_MIN_STICKERS) {
    throw new StickerManifestError({ tag: "emptyPack" });
  }
  if (stickers.length > STICKER_PACK_MAX_STICKERS) {
    throw new StickerManifestError({
      tag: "tooManyStickers",
      count: stickers.length,
    });
  }

  // Validate unique IDs.
  const seen = new Set<string>();
  for (const s of stickers) {
    if (seen.has(s.id)) {
      throw new StickerManifestError({ tag: "duplicateStickerIds", id: s.id });
    }
    seen.add(s.id);
  }

  // Validate URLs.
  for (const s of stickers) {
    if (!validateProxyURL(s.url)) {
      throw new StickerManifestError({ tag: "invalidStickerURL", url: s.url });
    }
  }

  // Build canonical bytes and sign them.
  const canonical = buildCanonicalBytes({ name, stickers });

  const sigRaw = await crypto.subtle.sign(
    { name: "Ed25519" },
    authorPrivateKey,
    canonical,
  );
  const signature = new Uint8Array(sigRaw);

  // Final payload = canonical || signature (64 bytes) || authorPublicKey (32 bytes)
  const payload = new Uint8Array(canonical.length + 64 + 32);
  payload.set(canonical, 0);
  payload.set(signature, canonical.length);
  payload.set(authorPublicKeyBytes, canonical.length + 64);

  return STICKER_PACK_PREFIX + base64UrlEncode(payload);
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

/**
 * Decodes and verifies a `kaether:sticker:pack:v1:` wire-format string.
 *
 * Enforces all constraints:
 *  - Schema 0x01
 *  - 1–200 stickers
 *  - Unique sticker IDs
 *  - All sticker URLs pass `validateProxyURL`
 *  - Ed25519 signature over canonical bytes verifies against embedded author key
 *
 * @param wire - Wire-format string produced by `encodeStickerPack`.
 * @returns Decoded and verified `StickerPack`.
 * @throws {StickerManifestError} On any validation failure.
 */
export async function decodeStickerPack(wire: string): Promise<StickerPack> {
  if (!wire.startsWith(STICKER_PACK_PREFIX)) {
    throw new StickerManifestError({ tag: "wrongPrefix" });
  }

  const b64 = wire.slice(STICKER_PACK_PREFIX.length);
  const raw = base64UrlDecode(b64);
  if (raw === null) {
    throw new StickerManifestError({
      tag: "malformed",
      reason: "base64url decode failed",
    });
  }

  // Minimum payload: schema(1) + nameLen(2) + name(0) + count(2) +
  //                  0 stickers + sig(64) + pubkey(32) = 101 bytes
  if (raw.length < 1 + 2 + 2 + 64 + 32) {
    throw new StickerManifestError({
      tag: "malformed",
      reason: `payload too short (${raw.length} bytes)`,
    });
  }

  // Extract author public key (last 32 bytes) and signature (32 bytes before that).
  const authorPublicKeyBytes = raw.slice(raw.length - 32);
  const signature = raw.slice(raw.length - 96, raw.length - 32);
  // Canonical bytes = everything before the 64-byte signature + 32-byte pubkey.
  const canonical = raw.slice(0, raw.length - 96);

  // Parse canonical bytes.
  if (canonical.length < 1) {
    throw new StickerManifestError({ tag: "malformed", reason: "empty canonical section" });
  }

  let offset = 0;

  const schema = canonical[offset++];
  if (schema !== STICKER_PACK_CURRENT_SCHEMA) {
    throw new StickerManifestError({ tag: "unsupportedSchema", schema });
  }

  if (offset + 2 > canonical.length) {
    throw new StickerManifestError({ tag: "malformed", reason: "truncated at nameLen" });
  }
  const nameLen = readUint16LE(canonical, offset);
  offset += 2;

  if (offset + nameLen > canonical.length) {
    throw new StickerManifestError({ tag: "malformed", reason: "truncated at name" });
  }
  let name: string;
  try {
    name = dec.decode(canonical.slice(offset, offset + nameLen));
  } catch {
    throw new StickerManifestError({ tag: "malformed", reason: "invalid UTF-8 in name" });
  }
  offset += nameLen;

  if (offset + 2 > canonical.length) {
    throw new StickerManifestError({ tag: "malformed", reason: "truncated at sticker count" });
  }
  const count = readUint16LE(canonical, offset);
  offset += 2;

  if (count < STICKER_PACK_MIN_STICKERS) {
    throw new StickerManifestError({ tag: "emptyPack" });
  }
  if (count > STICKER_PACK_MAX_STICKERS) {
    throw new StickerManifestError({ tag: "tooManyStickers", count });
  }

  const stickers: StickerEntry[] = [];
  const seenIds = new Set<string>();

  for (let i = 0; i < count; i++) {
    // id
    if (offset + 2 > canonical.length) {
      throw new StickerManifestError({ tag: "malformed", reason: `truncated at sticker[${i}] idLen` });
    }
    const idLen = readUint16LE(canonical, offset);
    offset += 2;
    if (offset + idLen > canonical.length) {
      throw new StickerManifestError({ tag: "malformed", reason: `truncated at sticker[${i}] id` });
    }
    let id: string;
    try {
      id = dec.decode(canonical.slice(offset, offset + idLen));
    } catch {
      throw new StickerManifestError({ tag: "malformed", reason: `invalid UTF-8 in sticker[${i}] id` });
    }
    offset += idLen;

    // url
    if (offset + 2 > canonical.length) {
      throw new StickerManifestError({ tag: "malformed", reason: `truncated at sticker[${i}] urlLen` });
    }
    const urlLen = readUint16LE(canonical, offset);
    offset += 2;
    if (offset + urlLen > canonical.length) {
      throw new StickerManifestError({ tag: "malformed", reason: `truncated at sticker[${i}] url` });
    }
    let url: string;
    try {
      url = dec.decode(canonical.slice(offset, offset + urlLen));
    } catch {
      throw new StickerManifestError({ tag: "malformed", reason: `invalid UTF-8 in sticker[${i}] url` });
    }
    offset += urlLen;

    // altText
    if (offset + 2 > canonical.length) {
      throw new StickerManifestError({ tag: "malformed", reason: `truncated at sticker[${i}] altTextLen` });
    }
    const altLen = readUint16LE(canonical, offset);
    offset += 2;
    if (offset + altLen > canonical.length) {
      throw new StickerManifestError({ tag: "malformed", reason: `truncated at sticker[${i}] altText` });
    }
    let altText: string;
    try {
      altText = altLen > 0 ? dec.decode(canonical.slice(offset, offset + altLen)) : "";
    } catch {
      throw new StickerManifestError({ tag: "malformed", reason: `invalid UTF-8 in sticker[${i}] altText` });
    }
    offset += altLen;

    // Uniqueness check.
    if (seenIds.has(id)) {
      throw new StickerManifestError({ tag: "duplicateStickerIds", id });
    }
    seenIds.add(id);

    // URL validation.
    if (!validateProxyURL(url)) {
      throw new StickerManifestError({ tag: "invalidStickerURL", url });
    }

    stickers.push({ id, url, altText });
  }

  if (offset !== canonical.length) {
    throw new StickerManifestError({
      tag: "malformed",
      reason: `trailing bytes in canonical section (${canonical.length - offset} unexpected)`,
    });
  }

  // Verify signature.
  let authorPublicKey: CryptoKey;
  try {
    authorPublicKey = await importEd25519PublicKey(authorPublicKeyBytes);
  } catch {
    throw new StickerManifestError({
      tag: "malformed",
      reason: "could not import author public key",
    });
  }

  const valid = await crypto.subtle.verify(
    { name: "Ed25519" },
    authorPublicKey,
    signature,
    canonical,
  );

  if (!valid) {
    throw new StickerManifestError({ tag: "invalidSignature" });
  }

  return {
    name,
    stickers,
    authorPublicKey: authorPublicKeyBytes,
    signature,
  };
}

/**
 * Returns `true` if `wire` has the sticker pack prefix (fast prefix check
 * without full decode).
 */
export function isStickerPack(wire: string): boolean {
  return wire.startsWith(STICKER_PACK_PREFIX);
}

/**
 * `decodeStickerPack` but returns `null` instead of throwing if the wire
 * string does not have the sticker pack prefix. Full validation errors still
 * throw.
 */
export async function tryDecodeStickerPack(
  wire: string,
): Promise<StickerPack | null> {
  if (!isStickerPack(wire)) {
    return null;
  }
  return decodeStickerPack(wire);
}
